// @ts-strict-ignore
import {baseComparator, sorted} from 'common/util/array';
import {renameEvent} from 'common/report/util/events';
import {propertiesUtils, renamePropertyValue} from 'common/report/util/properties';
import {PropertyType, ResourceType, SESSION_EVENTS} from 'common/report/constants';
import {LocalizationStore} from 'common/i18n/store';

import {ArbDimensionProperty, ArbPropertiesMap, ArbStandardProperty} from './types';

export const EVENT_SORT_TYPE = {
  ALPHA: `alpha`,
  USAGE: `usage`,
};

export function processEventNames(eventNames: string[], {sortType = EVENT_SORT_TYPE.ALPHA}) {
  let items = eventNames
    .filter((name) => name !== `$web_event`)
    .map((name) => ({label: renameEvent(name), name, custom: false}));
  if (sortType === EVENT_SORT_TYPE.ALPHA) {
    items = items.sort(baseComparator<{label: string}, string>({transform: (item) => item.label.toLowerCase()}));
  }
  return items;
}

const IGNORED_PROPERTIES = new Set([`$transactions`, `mp_event_id`, `$mp_visual_journeys`]);

type PropertiesToAdd = {
  name: string;
  label: string;
  /** Specifies the lookup table or b2b group this property belongs to */
  dataGroupId?: string;
};
type ProcessedArbStandardProperty = ArbStandardProperty & PropertiesToAdd;
type ProcessedArbDimensionProperty = Omit<ArbDimensionProperty, `join_property_type` | `dimension_data_group_id`> &
  PropertiesToAdd & {
    joinPropertyType: PropertyType;
    /** Specifies the lookup table this property is mapped to */
    dimensionDataGroupId: string;
  };
export type ProcessedArbProperty = ProcessedArbStandardProperty | ProcessedArbDimensionProperty;

export function processProperties(
  properties: ArbPropertiesMap,
  resourceType: ResourceType,
  i18n: LocalizationStore,
  options: {dataGroupId?: string} = {},
): ProcessedArbProperty[] {
  const {dataGroupId} = options;
  const propertyTypes = Object.values(PropertyType);
  const mappedProperties = Object.entries(properties)
    .filter(([name, details]) => !IGNORED_PROPERTIES.has(name) && propertyTypes.includes(details.type))
    .map(([name, details]) => {
      const processed = {
        count: details.count,
        label: propertiesUtils.renameProperty(name, resourceType, i18n, {dataGroupId}),
        name,
        ...(dataGroupId ? {dataGroupId} : {}),
      };

      if (details.type === PropertyType.Dimension) {
        return {
          ...processed,
          type: PropertyType.Dimension,
          joinPropertyType: details.join_property_type,
          dimensionDataGroupId: details.dimension_data_group_id,
        };
      } else {
        return {
          ...processed,
          type: details.type,
        };
      }
    });

  return mappedProperties.sort(
    baseComparator<{label: string}, string>({
      transform: (item) => item.label.toLowerCase(),
    }),
  );
}

export function processPropertyValues(propertyValues: string[], {propertyName}) {
  return sorted(propertyValues ?? [], {
    transform: (value) => renamePropertyValue(value, propertyName).toLowerCase(),
  });
}

/**
 * Generates the "event" param for some API endpoints which
 * can be a single event string or JSON encoded list of events
 * @param {string|string[]} value
 */
export function getEventParam(value) {
  let events = Array.isArray(value) ? value : [];

  if (!Array.isArray(value) && value !== null) {
    events = [value];
  }

  const [event, sessionEvent] = events.reduce(
    ([ev, sessionEv], val) => {
      if (SESSION_EVENTS[val]) {
        return [ev, [...sessionEv, SESSION_EVENTS[val].session_event]];
      }

      return [[...ev, val], sessionEv];
    },
    [[], []],
  );

  return {
    ...(event.length
      ? {
          event: JSON.stringify(event),
        }
      : {}),
    ...(sessionEvent.length
      ? {
          session_event: JSON.stringify(sessionEvent),
        }
      : {}),
  };
}
